import axios from "axios";
import instance from "lib/axiosInterceptor";
import { baseUrl } from "utils/baseUrl";
import {
  GetBriefcasesParams,
  GetBriefcasesResponse,
  GetBriefcaseParams,
  GetBriefcaseResponse,
  CreateFolderPayload,
  UpdateFolderPayload,
} from "interfaces";

function normalizeUsersFilterParams(
  filterParams: GetBriefcasesParams,
): GetBriefcasesParams {
  if (filterParams.startDate && filterParams.endDate) {
    return {
      ...filterParams,
      range: 5,
    };
  }

  return filterParams;
}

// ==================== GET ====================

export async function getBriefcasesService(
  params: GetBriefcasesParams,
): Promise<GetBriefcasesResponse> {
  const normalizedParams = normalizeUsersFilterParams(params);
  const response = await instance.get<GetBriefcasesResponse>("/Spaces", {
    params: normalizedParams,
  });
  return response.data;
}

export async function getBriefcaseService(
  params: GetBriefcaseParams,
): Promise<GetBriefcaseResponse> {
  const response = await instance.get<GetBriefcaseResponse>(
    `/Spaces/Get/${params.spaceId}?teamId=${params.teamId}`,
  );
  return response.data;
}

export async function getBriefcaseDocumentsService(
  spaceId: string,
  preview: boolean = false,
): Promise<any> {
  const response = await instance.get(
    `/Spaces/Documents/${spaceId}?preview=${preview}`,
  );
  return response.data;
}

export async function getBriefcaseDocumentService(
  documentId: string,
): Promise<any> {
  const response = await instance.get(`/Document/${documentId}`);
  return response.data;
}

export async function getBriefcaseShareHistoryService(
  spaceId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Spaces/History/${spaceId}?type=0`,
  );
  return response.data;
}

export async function getBriefcasePresentationHistoryService(
  spaceId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Spaces/History/${spaceId}?type=1`,
  );
  return response.data;
}

export async function getDocumentVisitsService(
  spaceId: string,
  documentId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Document/Visits/${documentId}?spaceId=${spaceId}`,
  );
  return response.data;
}

export async function getDocumentAnalyticsService(
  spaceId: string,
  documentId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Document/Analytics/${documentId}?spaceId=${spaceId}`,
  );
  return response.data;
}

export async function getDocumentUsageService(
  spaceId: string,
  documentId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Document/Usage/${documentId}?spaceId=${spaceId}`,
  );
  return response.data;
}

export async function getDocumentPerformanceSummaryService(
  spaceId: string,
  documentId: string,
): Promise<any> {
  const response = await instance.get(
    `/Report/Document/PerformanceSummary/${documentId}?spaceId=${spaceId}`,
  );
  return response.data;
}

export async function getBriefcaseFoldersService(
  spaceId: string,
): Promise<any> {
  const response = await instance.get(`/Spaces/Groups/${spaceId}`);
  return response.data;
}

export async function getBriefcaseFolderService(
  spaceId: string,
  groupId: string,
): Promise<any> {
  const response = await instance.get(`/Spaces/Groups/${spaceId}/${groupId}`);
  return response.data;
}

export async function getBriefcasePreviewDocumentsService(
  spaceId: string,
): Promise<any> {
  const response = await instance.get(
    `/Spaces/Documents/${spaceId}?preview=true`,
  );
  return response.data;
}

export async function getBriefcasePresentationDetailsPublicService(
  presentationId: string,
): Promise<any> {
  const response = await axios.get(`${baseUrl}/Presentation/${presentationId}`);
  return response.data;
}

// ==================== POST ====================

export async function duplicateBriefcaseService(
  spaceId: string,
  payload: { title: string; teamId: string; copyPrivateDocuments: boolean },
): Promise<{ spaceId: string }> {
  const response = await instance.post<{ spaceId: string }>(
    `/Spaces/Duplicate/${spaceId}`,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function createBriefcaseService(
  formData: FormData,
): Promise<{ spaceId: string }> {
  const response = await instance.post<{ spaceId: string }>(
    "/Spaces/Create",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
}

export async function updateBriefcaseService(formData: FormData): Promise<any> {
  const response = await instance.post("/Spaces/Update", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

export async function removeBriefcaseFileService(
  spaceId: string,
  documentId: string,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Documents/Remove/${spaceId}?documentId=${documentId}`,
  );
  return response.data;
}

export async function setBriefcaseDocumentPermissionService(
  spaceId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Documents/SetPermission/${spaceId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function removeBriefcaseService(
  spaceId: string,
  archive: boolean,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Delete/${spaceId}?archive=${archive}`,
    {},
  );
  return response.data;
}

export async function createBriefcaseFolderService(
  payload: CreateFolderPayload,
): Promise<any> {
  const response = await instance.post("/Spaces/Groups/Create/", payload);
  return response.data;
}

export async function updateBriefcaseFolderService(
  groupId: string,
  payload: Omit<UpdateFolderPayload, "groupId">,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Groups/Update/${groupId}`,
    payload,
  );
  return response.data;
}

export async function deleteBriefcaseFolderService(
  spaceId: string,
  groupId: string,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Groups/Delete/${groupId}?spaceId=${spaceId}`,
  );
  return response.data;
}

export async function shareNewBriefcasePresentationService(
  data: any,
): Promise<any> {
  const response = await instance.post("Presentation/New", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
}

export async function updateBriefcaseDocumentsService(
  spaceId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Documents/Update/${spaceId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function updateBriefcaseFoldersPositionsService(
  spaceId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Groups/UpdatePositions/?spaceId=${spaceId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function addBriefcaseMemberService(
  spaceId: string,
  memberId: string,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/AddMember/${spaceId}?memberId=${memberId}`,
  );
  return response.data;
}

export async function removeBriefcaseMemberService(
  spaceId: string,
  memberId: string,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/RemoveMember/${spaceId}?memberId=${memberId}`,
  );
  return response.data;
}

export async function updateBriefcasePresentationService(
  presentationId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Presentation/Update/${presentationId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function addBriefcasePresentationGuestsService(
  presentationId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Presentation/AddGuests/${presentationId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function removeBriefcasePresentationGuestsService(
  presentationId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Presentation/RemoveGuests/${presentationId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}

export async function renameBriefcaseDocumentService(
  documentId: string,
  data: any,
): Promise<any> {
  const response = await instance.post(
    `/Spaces/Documents/Rename/${documentId}`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
}
